import { useMemo, useEffect } from 'react';
import isNil from 'lodash/isNil';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { useOgTenantInfo } from '@atlassian/jira-eoc-tenant-info/src/controllers/index.tsx';
import { useContainerContext } from '@atlassian/jira-providers-container-context/src/main.tsx';
import { usePathParam, useRouter } from '@atlassian/jira-router';
import { useIsItsmTemplateProject } from '@atlassian/jira-router-resources-service-desk-is-itsm-project/src/controllers/index.tsx';
import { SEEN } from '@atlassian/jira-servicedesk-itsm-fsf-tour-state/src/common/constants.tsx';
import { useTourState as useFsfTourState } from '@atlassian/jira-servicedesk-itsm-fsf-tour-state/src/main.tsx';
import { useIsItsmSampleProject } from '@atlassian/jira-servicedesk-itsm-sample-project/src/services/is-itsm-sample-project/index.tsx';
import {
	toBaseUrl,
	toProjectId,
	type BaseUrl,
	type AccountId,
	type CloudId,
} from '@atlassian/jira-shared-types/src/general.tsx';
import { useTenantContext } from '@atlassian/jira-tenant-context-controller/src/components/tenant-context/index.tsx';
import {
	ActiveState,
	HasSeenReopenSpotlight,
	VisualState,
} from '@atlassian/jsm-getting-started-panel';
import { createHook, createStore, type BoundActions } from '@atlassian/react-sweet-state';
import { type Actions, type StoreActions, actions as subscriberActions } from './actions';
import type { State, GettingStartedPanelState, OpsTeamModeState } from './types';

const initialState: State = {
	data: undefined,
	isGspInitialized: true,
	isSaving: false,
	shouldRecordPerformanceExperiences: true,
};

export const store = createStore<State, StoreActions>({
	name: 'GettingStartedPanelState',
	initialState,
	actions: subscriberActions,
});

const useGettingStartedPanelStoreState = createHook(store, {
	selector: ({ data, isGspInitialized, shouldRecordPerformanceExperiences }: State) => {
		const canRender = isGspInitialized && data?.properties.user.activeState === ActiveState.On;
		const canRenderPanel = canRender && data?.properties.user.visualState === VisualState.FullPanel;
		const canRenderOpenButton =
			canRender && data?.properties.user.visualState === VisualState.Lozenge;
		const canRenderReopenButton =
			isGspInitialized && data?.properties.user.activeState === ActiveState.Off;
		const canRenderReopenSpotlight =
			canRenderReopenButton &&
			data?.properties.user.hasSeenReopenSpotlight === HasSeenReopenSpotlight.No;
		const canPollPanelState = data?.properties.user.activeState === ActiveState.On;

		return {
			data,
			canRenderPanel,
			canRenderOpenButton,
			canRenderReopenButton,
			canRenderReopenSpotlight,
			canPollPanelState,
			shouldRecordPerformanceExperiences,
		};
	},
});

const useGettingStartedPanelStoreActions = createHook(store, {
	selector: null,
});

/**
 * Hook returns if FSF tour is seen
 */
const useIsProjectTourSeen = () => {
	const [{ data, isLoading, error }, { getTourState }] = useFsfTourState();
	const isError = !isNil(error);
	const hasData = !isNil(data);

	useEffect(() => {
		if (!hasData && !isLoading && !isError) {
			getTourState();
		}
	}, [hasData, isLoading, isError, getTourState]);

	return data?.seenProjectTour === SEEN;
};

/**
 * Hook returns if ITSM sample project
 */
const useIsSampleProject = () => {
	const { data: isItsmSampleProject } = useIsItsmSampleProject();
	const [{ data: containerContext }] = useContainerContext();

	if (isItsmSampleProject != null) {
		return isItsmSampleProject;
	}
	/*
    When isItsmSampleProject is null or undefined - return null/undefined, but only if we are in project context
    (this is to identify that this var has not been initialized yet, see isItsmSampleProject usages, where we check for null/isNil);
    return false otherwise
 */
	return containerContext?.contextType === 'project' ? isItsmSampleProject : false;
};

/**
 * Hook returns if ITSM project and projectId
 */
export const useItsmProject = () => {
	const { data: isItsmProject } = useIsItsmTemplateProject();
	const [{ data: containerContext }] = useContainerContext();
	const projectId =
		containerContext?.contextType === 'project' && !isNil(containerContext.project)
			? toProjectId(String(containerContext.project.id))
			: undefined;

	return {
		isItsmProject: !isNil(projectId) && isItsmProject === true,
		projectId,
	};
};

/**
 * Hook returns boolean value for enableTeamMode and string teamId if exist.
 */
export const useOpsTeamMode = (): OpsTeamModeState => {
	const [
		{
			location: { pathname },
		},
	] = useRouter();

	const { shouldUseJSM } = useOgTenantInfo();

	const isOpsPage = pathname.startsWith('/jira/ops/teams/');

	const [teamId] = usePathParam('teamId');

	const enableOpsTeamMode = isOpsPage && teamId !== undefined && !!shouldUseJSM;

	return {
		teamId,
		enableOpsTeamMode,
	};
};

/**
 * Hook returns boolean value for enabling user centric mode
 */
export const useUserCentricMode = (): boolean => {
	const { isItsmProject } = useItsmProject();
	const { shouldUseJSM } = useOgTenantInfo();

	const enableUserCentricMode = isItsmProject && !!shouldUseJSM;

	return enableUserCentricMode;
};

type UseActionsMemoArg = BoundActions<State, StoreActions> & {
	baseUrl: BaseUrl;
	cloudId: CloudId;
	accountId: AccountId | null;
};

const useActionsMemo = ({
	baseUrl,
	cloudId,
	accountId,
	initializeState,
	reopenGettingStartedPanel,
	markReopenSpotlightAsSeen,
	fetchState,
	savePanelState,
	resolveTask,
	saveProperty,
	stopRecordingPerformanceExperiences,
	setOpsAdminTaskVisibility,
}: UseActionsMemoArg): Actions => {
	const gatewayBaseUrl = toBaseUrl(`${baseUrl}/gateway/api`);

	const isItsmSampleProject = useIsSampleProject();
	const { isItsmProject, projectId } = useItsmProject();
	const isProjectTourSeen = useIsProjectTourSeen();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { teamId, enableOpsTeamMode } = useOpsTeamMode();
	const enableUserCentricMode = useUserCentricMode();

	return useMemo(() => {
		const actionProps = {
			createAnalyticsEvent,
			baseUrl: gatewayBaseUrl,
			cloudId,
			accountId,
			isItsmSampleProject,
			isProjectTourSeen,
			isItsmProject,
			projectId,
			...(enableOpsTeamMode && { teamId, enableOpsTeamMode }),
			...(enableUserCentricMode && { enableUserCentricMode }),
		};
		return {
			initializeState: initializeState(actionProps),
			reopenGettingStartedPanel: reopenGettingStartedPanel(actionProps),
			markReopenSpotlightAsSeen: markReopenSpotlightAsSeen(actionProps),
			fetchState: fetchState(actionProps),
			savePanelState: savePanelState(actionProps),
			resolveTask: resolveTask(actionProps),
			saveProperty: saveProperty(actionProps),
			stopRecordingPerformanceExperiences: stopRecordingPerformanceExperiences(),
			setOpsAdminTaskVisibility: setOpsAdminTaskVisibility(actionProps),
		};
	}, [
		createAnalyticsEvent,
		gatewayBaseUrl,
		cloudId,
		accountId,
		isItsmSampleProject,
		isProjectTourSeen,
		isItsmProject,
		projectId,
		enableOpsTeamMode,
		teamId,
		enableUserCentricMode,
		initializeState,
		reopenGettingStartedPanel,
		markReopenSpotlightAsSeen,
		fetchState,
		savePanelState,
		resolveTask,
		saveProperty,
		stopRecordingPerformanceExperiences,
		setOpsAdminTaskVisibility,
	]);
};

const useStateMemo = ({
	data,
	canRenderPanel,
	canRenderOpenButton,
	canRenderReopenButton,
	canRenderReopenSpotlight,
	canPollPanelState,
	shouldRecordPerformanceExperiences,
}: GettingStartedPanelState): GettingStartedPanelState =>
	useMemo(
		() => ({
			data,
			canRenderPanel,
			canRenderOpenButton,
			canRenderReopenButton,
			canRenderReopenSpotlight,
			canPollPanelState,
			shouldRecordPerformanceExperiences,
		}),
		[
			data,
			canRenderPanel,
			canRenderOpenButton,
			canRenderReopenButton,
			canRenderReopenSpotlight,
			canPollPanelState,
			shouldRecordPerformanceExperiences,
		],
	);

export const useGettingStartedPanelState = (): [GettingStartedPanelState, Actions] => {
	const { cloudId, atlassianAccountId: accountId, baseUrl } = useTenantContext();
	const [state, actions] = useGettingStartedPanelStoreState();
	const memoizedActions = useActionsMemo({
		...actions,
		baseUrl,
		cloudId,
		accountId,
	});
	const memoizedState = useStateMemo(state);
	return useMemo(() => [memoizedState, memoizedActions], [memoizedActions, memoizedState]);
};

export const useGettingStartedPanelActions = (): [undefined, Actions] => {
	const { cloudId, atlassianAccountId: accountId, baseUrl } = useTenantContext();
	const [, actions] = useGettingStartedPanelStoreActions();
	const memoizedActions = useActionsMemo({
		...actions,
		baseUrl,
		cloudId,
		accountId,
	});
	return useMemo(() => [undefined, memoizedActions], [memoizedActions]);
};
